import { Injectable, inject } from '@angular/core';

import { EMPTY } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { environment } from '@rocksoft/environment';
import { CategoryVppApplicationConfigurationService } from '@rocksoft/settings-sdk';
import { CategoryConfigurationInheritedApiActions } from '../category-configuration-inherited/category-configuration-inherited.actions';
import { compareVppApplications, safeMap } from '../effects.helpers';
import {
  CategoryVppApplicationsApiActions,
  CategoryVppApplicationsViewActions,
} from './category-vpp-applications.actions';

@Injectable()
export class CategoryVppApplicationsEffects {
  private readonly actions = inject(Actions);
  private readonly categoryVppApplicationService = inject(CategoryVppApplicationConfigurationService);

  protected features = environment.features;

  /**
   * The category vpp application configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   * On success, we request a reload of the category-configuration-inherited store (read-only store).
   */
  categoryVppApplicationLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsViewActions.applicationConfigurationsUpdated),
      mergeMap((action) => {
        const categoryType = action.categoryType;
        const operation = compareVppApplications(action);

        if (operation.length === 0) {
          return EMPTY;
        }

        return safeMap(
          this.categoryVppApplicationService.updateAllVppApplicationsForCategory({
            categoryType,
            categoryId: action.categoryId,
            operation,
          }),
          () =>
            CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
              categoryId: action.categoryId,
            }),
          (error) =>
            CategoryVppApplicationsApiActions.applicationConfigurationsUpdatedError({
              categoryId: action.categoryId,
              categoryType: action.categoryType,
              error,
            })
        );
      })
    );
  });

  /**
   * The vpp applications couldn't be updated.
   * Effect: we request a reload of the category-configuration-inherited store (read-only store) from the api.
   */
  categoryVppApplicationsUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsApiActions.applicationConfigurationsUpdatedError),
      map((action) =>
        CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
          categoryId: action.categoryId,
        })
      )
    );
  });
}
